import { useQuery } from '@apollo/client/react'
import capitalize from 'lodash-es/capitalize'
import { usePathname, useSearchParams } from 'next/navigation'
import { stringifyUrl } from 'query-string'
import { ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ReactElement, ReactNode, memo } from 'react'

import {
  ColorSelector,
  MediaAsContents,
  ResponsiveImageWithSkeletonWrapper,
  useIsBrowser,
  useLmfAnalyticsOnClick,
} from '@syconium/little-miss-figgy'

import { gql } from '../../../../../../../../__generated__/graphql/catalog'
import { CollectionPricingDisplay } from '../../../../../../../../__generated__/graphql/catalog/graphql'
import { derivePurchasedBadgeText } from '../../../../../../../../app/_components/badge/purchasedBadgeText'
import { useOwnedColorCountsForProduct } from '../../../../../../../../app/_components/badge/useOwnedColorCountsForProduct.client'
import { FloatingFavoritesButton } from '../../../../../../../../app/_components/buttons/FavoritesButton.client'
import { QuickBuyDialog } from '../../../../../../../../app/_components/dialogs/QuickbuyDialog.client'
import { NextLink } from '../../../../../../../../app/_components/navigation/NextLink'
import {
  PricingRange,
  derivePricingForVariantGroup,
} from '../../../../../../../../app/_components/pricing/PricingRange.client'
import { usePageTypeTracking } from '../../../../../../../../app/_hooks/useStaticTracking'
import { useFeatureFlag } from '../../../../../../../../app/_providers/ExperimentationProvider.client'
import { useSiteWideMessaging } from '../../../../../../../../app/_providers/SiteWideMessagingProvider.client'
import {
  resolveSecondaryLocation,
  useTrackingLocation,
} from '../../../../../../../../app/_providers/TrackingLocationProvider.client'
import { useTrackProductInteraction } from '../../../../../../../../app/_providers/TrackingProvider.client'
import { useTranslation } from '../../../../../../../../app/_providers/TranslationProvider.client'
import { Fit } from '../../../../../../../../brunswick/constants'
import { useGoogleTagManager } from '../../../../../../../../lib/hooks/useGoogleTagManager'
import { useInputObservingState } from '../../../../../../../../lib/hooks/useInputObservingState'
import { formatPrettyColorName, sortColorsByAvailability } from '../../../../../../../../lib/utils'
import { useCollectionFilterQueryParamsContext } from '../../../../../../../containers/CollectionFilterQueryParamsContext/CollectionFilterQueryParamsContext'
import { IconBadge } from '../../../../../../IconBadge/IconBadge'
import { CollectionItemVariantGroup } from '../../../../hooks/useCollectionGridSectionItems'
import { ColumnLayoutService } from '../../containers/ColumnLayoutService'

import {
  Badge,
  Body,
  Color,
  ColorLine,
  ColorSelectorWrapper,
  DetailsContents,
  FitLine,
  ImageContainer,
  ImageContents,
  PriceDisplayWrapper,
  PrimaryImageWrapper,
  ProductName,
  PurchasedBadge,
  SecondaryImageWrapper,
  SelectColorsOnSaleBadge,
  ShowMore,
  ShowMoreWrap,
  SiteWidePromoWrap,
  StyledIconBadge,
  UnavailableOverlay,
} from './styles'

export type TileVariantGroupCollectionScope = {
  collectionHandle: string
  pricingDisplay: CollectionPricingDisplay
}

export type TileVariantGroup = CollectionItemVariantGroup & {
  collectionScope: TileVariantGroupCollectionScope | undefined
}

export interface ProductGroupTileProps {
  tileVariantGroup: TileVariantGroup
  index: number
}

const MAX_PREVIEW_COLORS = 4
const MAX_VISIBLE_BADGES_IN_HIERARCHY_VARIANT = 2

// Badges compete for limited space on the tile, so only the highest priority ones render
const selectVisibleBadges = (entries: { badge: ReactNode; priority: number }[]): ReactNode[] => {
  const visible = new Set(
    entries
      .filter(entry => entry.badge)
      .sort((a, b) => a.priority - b.priority)
      .slice(0, MAX_VISIBLE_BADGES_IN_HIERARCHY_VARIANT)
  )
  return entries.filter(entry => visible.has(entry)).map(entry => entry.badge)
}

const ProductGroupTileIconBadge = (iconBadge: IconBadge) => {
  const isBrowser = useIsBrowser({ ssr: true })
  return isBrowser ? (
    <>
      <MediaAsContents lessThan='md'>
        <StyledIconBadge {...iconBadge} iconBadgePlacement={'top left'} size={'small'} />
      </MediaAsContents>
      <MediaAsContents greaterThanOrEqual='md'>
        <StyledIconBadge {...iconBadge} iconBadgePlacement={'top left'} size={'large'} />
      </MediaAsContents>
    </>
  ) : null
}

export const ProductGroupTile = memo(
  ({ tileVariantGroup: initialTileVariantGroup, index }: ProductGroupTileProps): ReactElement => {
    const { numberOfColumnsOnMobile, numberOfColumnsOnDesktop } = ColumnLayoutService.useContainer()
    const { pushProductClick } = useGoogleTagManager()
    const { trackProductInteraction } = useTrackProductInteraction()
    const pageType = usePageTypeTracking()
    const trackingLocation = useTrackingLocation()
    const { siteWidePromotionMessage: siteWidePromo } = useSiteWideMessaging()
    const { selectedFilters } = useCollectionFilterQueryParamsContext()
    const {
      collections: { productGroupTile: strings },
      quickBuy: { soldOutOverlayMessage, waitlistOverlayMessage },
      pdp: { purchasedBadge: purchasedBadgeStrings },
    } = useTranslation()
    const pathname = usePathname()
    const isPortal = pathname?.includes('/portals/')

    const isTileHierarchyVariant =
      useFeatureFlag<'true' | 'false'>({
        key: 'plp-tile-hierarchy',
        controlVariant: 'false',
        ifAccessedPriorToDecisionInitialization: 'return-null-while-pending',
      }) === 'true'

    /** Generic data: Data that is static so it can be pulled from the initial data. */
    const isCriticalImage = index < 4
    const variantGroup = initialTileVariantGroup
    const title = variantGroup.title
    const productGroupHandle = variantGroup.product.handle
    const isKit = variantGroup.product.__typename === 'ProductCatalogKit'

    /** User controlled fields. */
    const { state: selectedColor, setState: setSelectedColor } = useInputObservingState<
      | {
          id: string
          displayValue: string
        }
      | undefined
    >(initialTileVariantGroup.color ?? undefined)

    const [hasColorChanged, setHasColorChanged] = useState(false)
    const onColorSelected = useCallback(
      (selectedColor: Parameters<typeof setSelectedColor>[0]) => {
        setSelectedColor(selectedColor)
        setHasColorChanged(true)
      },
      [setSelectedColor]
    )
    const hasColorFilter = useMemo(
      () => (selectedFilters.hasOwnProperty('color') ? true : false),
      [selectedFilters]
    )

    const shouldFetchUpdatedTileVariantGroup = useMemo(() => {
      if (selectedColor?.id && initialTileVariantGroup.color?.id !== selectedColor.id) return true
      return false
    }, [initialTileVariantGroup, selectedColor])

    const {
      data: updatedTileVariantGroupCurrentData,
      previousData: updatedTileVariantGroupPreviousData,
      loading: loadingTileVariantGroup,
    } = useQuery(
      gql(`
          query GetCollectionTileVariantGroup(
            $handle: String!
            $color: String
            $collectionHandle: String
          ) {
            productCatalogProduct(handle: $handle) {
              id
              __typename
              variantGroup(color: $color) {
                ...CollectionItemVariantGroup
              }
            }
          }
        `),
      {
        variables: {
          color: selectedColor?.id,
          handle: productGroupHandle,
          collectionHandle: initialTileVariantGroup.collectionScope?.collectionHandle,
        },
        skip: !shouldFetchUpdatedTileVariantGroup,
      }
    )

    const updatedTileVariantGroup =
      updatedTileVariantGroupCurrentData ?? updatedTileVariantGroupPreviousData
    const tileVariantGroup = shouldFetchUpdatedTileVariantGroup
      ? updatedTileVariantGroup?.productCatalogProduct.variantGroup ?? initialTileVariantGroup
      : initialTileVariantGroup
    const collectionBadgeColor = tileVariantGroup.plpBadge?.colorHexCode
    const collectionBadgeText = tileVariantGroup.plpBadge?.text
    const swPromoEligible = tileVariantGroup.siteWidePromoEligible

    const selectedColorSoldOut = tileVariantGroup.stockState === 'OUT_OF_STOCK'
    const images = useMemo(() => {
      return tileVariantGroup.images ?? []
    }, [tileVariantGroup.images])
    const imageSource = images[0]?.url
    const secondaryImageSource = images[1]?.url
    const iconBadge = tileVariantGroup.iconBadge

    const unavailableOverlayMessage =
      tileVariantGroup.availability === 'WAIT_LISTING'
        ? waitlistOverlayMessage
        : soldOutOverlayMessage

    const previewColors: ComponentProps<typeof ColorSelector>['colors'] = useMemo(() => {
      const getPreviewSortPriority = (
        color: ComponentProps<typeof ColorSelector>['colors'][number]
      ) => {
        if (color.rawName === initialTileVariantGroup.color?.id) return 0 // Default color first
        if (color.soldOut === false) return 1 // Available second
        if (color.availability === 'core') return 2 // Core colors third
        return 3 // All others (e.g., limited-edition, waitlist/out of stock)
      }

      const colors = initialTileVariantGroup.product.options.colors.map(colorOption => ({
        availability: colorOption.color.group?.id ?? '',
        hexCodes: colorOption.color.hexCodes,
        name: colorOption.color.displayValue,
        rawName: colorOption.color.id,
        soldOut: colorOption.outOfStock,
      }))

      // Group ordering first (core before limited edition), then the preview tiers on top of it —
      // sort is stable, so group order survives as the within-tier tiebreak.
      return sortColorsByAvailability(colors)
        .sort((a, b) => getPreviewSortPriority(a) - getPreviewSortPriority(b))
        .slice(0, MAX_PREVIEW_COLORS)
    }, [initialTileVariantGroup.color?.id, initialTileVariantGroup.product.options.colors])

    const displayPreviewColors = !isKit && previewColors.length > 1

    // plain=true query param is used by iOS app to view webpages within a webview without the website's header/footer
    const plainQueryParamValue = useSearchParams()?.get('plain') ?? undefined
    const pdpLinkPath = stringifyUrl({
      url: tileVariantGroup.urlPath,
      query: { plain: plainQueryParamValue },
    })

    const onProductLinkClicked = useCallback(() => {
      trackProductInteraction({
        handle: productGroupHandle,
        color: selectedColor?.id,
        id: tileVariantGroup.id,
        shopifyId: tileVariantGroup.defaultShopifyProduct.id,
        soldOut: tileVariantGroup.stockState === 'OUT_OF_STOCK',
        tracking: {
          location: pageType,
          secondary_location: resolveSecondaryLocation(trackingLocation),
          section_number: trackingLocation.sectionNumber ?? null,
          position: trackingLocation.position ?? null,
          href: pdpLinkPath,
        },
      })
      pushProductClick({
        variant: {
          shopifyId: tileVariantGroup.defaultShopifyProduct.defaultShopifyVariant.id,
          shopifyProductId: tileVariantGroup.defaultShopifyProduct.id,
          sku: tileVariantGroup.defaultShopifyProduct.defaultShopifyVariant.sku,
          price: tileVariantGroup.priceRange.min,
          compareAtPrice: tileVariantGroup.compareAtPriceRange?.min ?? null,
          variantGroup,
          product: variantGroup.product,
        },
        list:
          initialTileVariantGroup.collectionScope?.collectionHandle ??
          tileVariantGroup.associatedExclusiveCollectionHandle ??
          pathname ??
          undefined,
        position: index,
      })
    }, [
      index,
      initialTileVariantGroup.collectionScope?.collectionHandle,
      pageType,
      pathname,
      pdpLinkPath,
      productGroupHandle,
      pushProductClick,
      selectedColor?.id,
      tileVariantGroup.associatedExclusiveCollectionHandle,
      tileVariantGroup.compareAtPriceRange?.min,
      tileVariantGroup.defaultShopifyProduct.defaultShopifyVariant.id,
      tileVariantGroup.defaultShopifyProduct.defaultShopifyVariant.sku,
      tileVariantGroup.defaultShopifyProduct.id,
      tileVariantGroup.id,
      tileVariantGroup.priceRange.min,
      tileVariantGroup.stockState,
      trackProductInteraction,
      trackingLocation,
      variantGroup,
    ])

    const onShowMoreClickedWithTracking = useLmfAnalyticsOnClick({
      element: 'link',
      href: pdpLinkPath,
      label: 'Show More',
      variations: [],
      onClick: onProductLinkClicked,
    })

    const colorDisplayName = selectedColor?.displayValue
    const imageAltText = colorDisplayName
      ? `${title} - ${formatPrettyColorName(colorDisplayName)}`
      : title
    const displayedColorKey = selectedColor?.id ?? tileVariantGroup.color?.id
    const displayedFitKey = tileVariantGroup.defaultFit?.id ?? Fit.REGULAR

    const fitsAvailabilityEndingText: string | undefined = tileVariantGroup.fits
      .filter(fit => fit.id !== Fit.REGULAR)
      .map(fit => capitalize(fit.displayValue))
      .join(' & ')
    const fitsAvailabilityText: string | null =
      fitsAvailabilityEndingText && !isKit
        ? strings.fitsAvailabilityBeginningText.replace('{{size}}', fitsAvailabilityEndingText)
        : null

    const lastInteractedShopifyIdRef = useRef<string | null>(null)
    useEffect(() => {
      if (
        !loadingTileVariantGroup &&
        // TODO: Address this in a future refactor
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        hasColorChanged &&
        lastInteractedShopifyIdRef.current !==
          // TODO: Address this in a future refactor
          // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
          tileVariantGroup.defaultShopifyProduct.id
      ) {
        lastInteractedShopifyIdRef.current = tileVariantGroup.defaultShopifyProduct.id
        trackProductInteraction({
          handle: productGroupHandle,
          color: selectedColor?.id,
          id: tileVariantGroup.id,
          shopifyId: tileVariantGroup.defaultShopifyProduct.id,
          soldOut: tileVariantGroup.stockState === 'OUT_OF_STOCK',
        })
      }
    }, [
      hasColorChanged,
      loadingTileVariantGroup,
      productGroupHandle,
      selectedColor?.id,
      tileVariantGroup.defaultShopifyProduct.id,
      tileVariantGroup.id,
      tileVariantGroup.stockState,
      trackProductInteraction,
    ])

    const merchandisedToOnlyShowVariantGroupPrice =
      initialTileVariantGroup.collectionScope?.pricingDisplay === 'defaultProductPrice'
    const isSearchedExclusive =
      initialTileVariantGroup.exclusive &&
      !initialTileVariantGroup.collectionScope?.collectionHandle
    const showVariantGroupPrice =
      merchandisedToOnlyShowVariantGroupPrice ||
      isSearchedExclusive ||
      hasColorChanged ||
      hasColorFilter
    const displaySelectColorsSaleBadge = Boolean(
      initialTileVariantGroup.compareAtPriceRange &&
        (merchandisedToOnlyShowVariantGroupPrice || isSearchedExclusive)
    )

    const pricingRange = useMemo(() => {
      // Portal users see discounts in checkout, not on their portal page.
      if (isPortal) {
        return derivePricingForVariantGroup({
          priceRange: tileVariantGroup.compareAtPriceRange ?? tileVariantGroup.priceRange,
          compareAtPriceRange: null,
        })
      }

      if (showVariantGroupPrice) return derivePricingForVariantGroup(tileVariantGroup)
      return derivePricingForVariantGroup(tileVariantGroup.product.priceRanges)
    }, [isPortal, showVariantGroupPrice, tileVariantGroup])

    const badgeDisplay = collectionBadgeText ? (
      <Badge
        key='badge'
        colorOverride={collectionBadgeColor}
        textTransform={isTileHierarchyVariant ? 'capitalize' : 'uppercase'}
        $hierarchyVariant={isTileHierarchyVariant}
      >
        {collectionBadgeText}
      </Badge>
    ) : null

    // The purchased badge is only part of the tile hierarchy test
    const ownedColorCounts = useOwnedColorCountsForProduct(productGroupHandle, {
      skip: !isTileHierarchyVariant,
    })
    const purchasedBadgeText = useMemo(
      () =>
        derivePurchasedBadgeText(ownedColorCounts, displayedColorKey ?? '', purchasedBadgeStrings),
      [ownedColorCounts, displayedColorKey, purchasedBadgeStrings]
    )

    const purchasedBadgeDisplay =
      isTileHierarchyVariant && purchasedBadgeText ? (
        <PurchasedBadge key='purchasedBadge' $hierarchyVariant={isTileHierarchyVariant}>
          {purchasedBadgeText}
        </PurchasedBadge>
      ) : null

    const productNameDisplay = (
      <NextLink key='productName' title={title} href={pdpLinkPath} onClick={onProductLinkClicked}>
        <ProductName $hierarchyVariant={isTileHierarchyVariant}>{title}</ProductName>
      </NextLink>
    )

    const colorLine = (
      <ColorLine key='colorLine'>
        {colorDisplayName && !isKit && <Color>{formatPrettyColorName(colorDisplayName)}</Color>}
      </ColorLine>
    )

    const promoDisplay = displaySelectColorsSaleBadge ? (
      <SelectColorsOnSaleBadge key='selectColorsOnSaleBadge'>
        {strings.selectColorsOnSale}
      </SelectColorsOnSaleBadge>
    ) : (
      siteWidePromo &&
      swPromoEligible && <SiteWidePromoWrap key='siteWidePromo'>{siteWidePromo}</SiteWidePromoWrap>
    )

    const priceDisplay = (
      <PriceDisplayWrapper key='priceDisplay'>
        <PricingRange {...pricingRange} />
      </PriceDisplayWrapper>
    )

    const colorSelectorDisplay = displayPreviewColors ? (
      <ColorSelectorWrapper key='colorSelector' $hierarchyVariant={isTileHierarchyVariant}>
        <ColorSelector
          colors={previewColors}
          onSelectColor={color =>
            onColorSelected({
              id: color.rawName,
              displayValue: color.name,
            })
          }
          selectedColorKey={selectedColor?.id}
          disableMobileHorizontalSwipe
          swatchSpacing='small'
        />
        {tileVariantGroup.product.options.colorsTotalCount > MAX_PREVIEW_COLORS && (
          <ShowMoreWrap>
            <NextLink
              href={pdpLinkPath}
              onClick={onShowMoreClickedWithTracking}
              style={{ display: 'inline-block' }}
            >
              <ShowMore $hierarchyVariant={isTileHierarchyVariant}>
                {isTileHierarchyVariant
                  ? `+${tileVariantGroup.product.options.colorsTotalCount}`
                  : strings.showMoreText.replace(
                      '{{colorAmount}}',
                      (
                        tileVariantGroup.product.options.colorsTotalCount - MAX_PREVIEW_COLORS
                      ).toString()
                    )}
              </ShowMore>
            </NextLink>
          </ShowMoreWrap>
        )}
      </ColorSelectorWrapper>
    ) : null

    const fitLineDisplay =
      fitsAvailabilityText && tileVariantGroup.primaryCategory.key !== 'SCRUB_PANTS' ? (
        <FitLine key='fitLine'>{fitsAvailabilityText}</FitLine>
      ) : null

    const visibleBadges = selectVisibleBadges([
      { badge: promoDisplay, priority: 1 },
      { badge: badgeDisplay, priority: 3 },
      { badge: purchasedBadgeDisplay, priority: 2 },
    ])

    return (
      <Body data-availability={selectedColorSoldOut ? 'unavailable' : 'available'}>
        <ImageContents>
          <ImageContainer>
            <NextLink
              href={pdpLinkPath}
              onClick={onProductLinkClicked}
              $display='block'
              $insetFocusOutline
            >
              {imageSource ? (
                <PrimaryImageWrapper>
                  <ResponsiveImageWithSkeletonWrapper
                    data-testid={`product-group-tile-image--${productGroupHandle}`}
                    alt={imageAltText}
                    loading={isCriticalImage ? 'default' : 'lazy'}
                    fetchPriority={isCriticalImage ? 'high' : undefined}
                    src={imageSource}
                    aspectRatios={{
                      sm: 20 / 26,
                      md: 20 / 26,
                    }}
                    widths={{
                      unit: 'vw',
                      sm: 100 / numberOfColumnsOnMobile,
                      md: 100 / numberOfColumnsOnDesktop,
                    }}
                  />
                </PrimaryImageWrapper>
              ) : null}
              {secondaryImageSource && (
                <SecondaryImageWrapper>
                  <ResponsiveImageWithSkeletonWrapper
                    alt=''
                    loading='lazy'
                    fetchPriority='low'
                    src={secondaryImageSource}
                    aspectRatios={{
                      sm: 20 / 26,
                      md: 20 / 26,
                    }}
                    widths={{
                      unit: 'vw',
                      sm: 100 / numberOfColumnsOnMobile,
                      md: 100 / numberOfColumnsOnDesktop,
                    }}
                  />
                </SecondaryImageWrapper>
              )}
              {iconBadge ? <ProductGroupTileIconBadge {...iconBadge} /> : null}
              <UnavailableOverlay
                show={selectedColorSoldOut}
                aria-hidden={selectedColorSoldOut ? 'false' : 'true'}
              >
                {unavailableOverlayMessage}
              </UnavailableOverlay>
            </NextLink>
            {tileVariantGroup.id && displayedColorKey && displayedFitKey && !isKit && (
              <FloatingFavoritesButton
                productGroupHandle={productGroupHandle}
                colorKey={displayedColorKey}
                fitKey={displayedFitKey}
                variantGroupId={tileVariantGroup.id}
                shopifyProductId={tileVariantGroup.defaultShopifyProduct.id}
                productGroupImageSrc={imageSource}
              />
            )}
            {!isKit && !isPortal && selectedColor?.id && (
              <QuickBuyDialog
                position='bottom-right'
                secondaryTrackingLocation='Core'
                productGroupHandle={productGroupHandle}
                collectionHandle={
                  initialTileVariantGroup.collectionScope?.collectionHandle ??
                  tileVariantGroup.associatedExclusiveCollectionHandle
                }
                gender={tileVariantGroup.gender}
                categoryKey={tileVariantGroup.primaryCategory.key}
                initialColor={selectedColor.id}
              />
            )}
          </ImageContainer>
          {!isTileHierarchyVariant && [badgeDisplay, purchasedBadgeDisplay, productNameDisplay]}
        </ImageContents>
        <DetailsContents>
          {isTileHierarchyVariant
            ? [colorSelectorDisplay, colorLine, productNameDisplay, priceDisplay, ...visibleBadges]
            : [priceDisplay, promoDisplay, colorLine, colorSelectorDisplay, fitLineDisplay]}
        </DetailsContents>
      </Body>
    )
  }
)
